import type { ReactNode } from 'react';
import { AlertTriangle, Inbox, Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * 列表类页面的三种状态：加载中 / 空 / 出错。
 *
 * 抽出来的理由不是"少写几行"，而是**保证每个页面都把三种状态说清楚**：
 * 最常见的粗糙实现是"数据为空就什么都不显示"，用户分不清
 * "还没有数据"和"加载失败"。这里强制调用方写明"空"是什么意思、
 * 以及空状态下该做什么（给一个行动入口）。
 */
export function LoadingBlock({ label = '加载中…', className }: { label?: string; className?: string }) {
  return (
    <div className={cn('flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground', className)}>
      <Loader2 aria-hidden className="size-4 animate-spin" />
      <span>{label}</span>
    </div>
  );
}

export function EmptyBlock({
  title,
  description,
  action,
  className,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col items-center gap-3 rounded-lg border border-dashed px-4 py-10 text-center', className)}>
      <Inbox aria-hidden className="size-6 text-muted-foreground" />
      <div>
        <p className="text-sm font-medium">{title}</p>
        {description && <p className="mt-1 text-xs text-muted-foreground">{description}</p>}
      </div>
      {action}
    </div>
  );
}

export function ErrorBlock({
  message,
  onRetry,
  className,
}: {
  message: string;
  onRetry?: () => void;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col items-center gap-3 rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-8 text-center', className)}>
      <AlertTriangle aria-hidden className="size-5 text-destructive" />
      <p role="alert" className="text-sm text-destructive">
        {message}
      </p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="rounded-md border px-3 py-1.5 text-xs hover:bg-accent hover:text-accent-foreground"
        >
          重试
        </button>
      )}
    </div>
  );
}

/**
 * 分页控件。
 *
 * 页码直接跳转而不是无限滚动：文章与生词本都是"要回头找那条记录"的场景，
 * 无限滚动会让"第 3 页那个词"变得不可达。移动端上下滚动到底部点"下一页"仍然顺手。
 */
export function Pagination({
  page,
  totalPages,
  onChange,
  className,
}: {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
  className?: string;
}) {
  if (totalPages <= 1) return null;
  return (
    <nav className={cn('flex items-center justify-center gap-3 py-4 text-sm', className)} aria-label="分页">
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
        className="rounded-md border px-3 py-1.5 disabled:opacity-40"
      >
        上一页
      </button>
      <span className="text-muted-foreground">
        第 {page} / {totalPages} 页
      </span>
      <button
        type="button"
        disabled={page >= totalPages}
        onClick={() => onChange(page + 1)}
        className="rounded-md border px-3 py-1.5 disabled:opacity-40"
      >
        下一页
      </button>
    </nav>
  );
}
